/*
 * Maintain webview flag, it repersent it's wrapped in which native webview,
 * or the web is just opened by regular browser.
 */

import { useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { useSelector, useDispatch } from 'react-redux'
import { stringify, parse } from 'qs'
import { isEmpty } from 'lodash-es'

import {
  setWebviewName,
  setWebviewDevice,
  setWebviewAppVersion,
  setWebviewOsVersion,
  webviewInitialized,
  selectWebview,
  setSyStaffCode,
  setAdditionalNote,
  setServiceAdminNote,
  setPriorityVenderIds,
} from 'store/webview/webviewSlice'
import { setUtm } from 'store/utm/utmSlice'
import {
  selectSelfConstruct,
  setReservationCode,
  setSelfConstructUserId,
} from 'store/selfConstruct/selfConstructSlice'
import {
  setPreselectVender,
  selectPreselectVender,
} from 'store/cache/cacheSlice'

export const WebviewDetector = ({ children }) => {
  // TODO: should detect both server side and client side
  const router = useRouter()
  const dispatch = useDispatch()
  useEffect(() => {
    if (router.isReady) {
      const {
        webview,
        device,
        appVersion,
        osVersion,
        utm_source,
        utm_medium,
        utm_campaign,
        utm_term,
        utm_content,
        syStaffCode,
        additionalNote, // 訂單備註
        serviceAdminNote, // 居家備註
        reservationCode, // 來源訂單ID（有加密），搭配自建單使用
        scUserId, // 會員ID（有加密），搭配自建單使用
        gclid, // Ｇoogle 廣告過來的
        pvid, // 優先指派廠商ID
        preselect_vender, // 預選廠商ID，下單時顯示廠商資訊並指派訂單給該廠商
      } = router.query
      if (webview) {
        dispatch(setWebviewName(webview))
        dispatch(setWebviewDevice(device))
        dispatch(setWebviewAppVersion(appVersion))
        dispatch(setWebviewOsVersion(osVersion))
      }
      if (utm_source) {
        dispatch(
          setUtm({
            source: utm_source,
            medium: utm_medium || '',
            campaign: utm_campaign || '',
            term: utm_term || '',
            content: utm_content || '',
          })
        )
      } else if (gclid) {
        // 若是 Ｇoogle 廣告過來的，且沒有 utm_source，補上 utm_source
        dispatch(
          setUtm({
            source: 'google',
            medium: 'cp',
          })
        )
      }

      if (syStaffCode) {
        dispatch(setSyStaffCode(syStaffCode))
      }
      if (additionalNote) {
        dispatch(setAdditionalNote(additionalNote))
      }
      if (serviceAdminNote) {
        dispatch(setServiceAdminNote(serviceAdminNote))
      }
      if (reservationCode) {
        dispatch(setReservationCode(reservationCode))
      }
      if (scUserId) {
        dispatch(setSelfConstructUserId(scUserId))
      }
      if (pvid) {
        const venderIds = typeof pvid === 'string' ? [pvid] : pvid
        dispatch(setPriorityVenderIds(venderIds))
      }
      if (preselect_vender) {
        dispatch(setPreselectVender(preselect_vender))
      }
      dispatch(webviewInitialized())
    }
  }, [router.isReady])

  const [nextRoute, setNextRoute] = useState('')
  const [nextRouteInfo, setNextRouteInfo] = useState({
    path: '',
    shallow: false,
  })

  const webviewFromRedux = useSelector(selectWebview)
  const selfConstruct = useSelector(selectSelfConstruct)
  const preselectVenderFromRedux = useSelector(selectPreselectVender)
  useEffect(() => {
    if (!router.isReady) {
      return
    }

    const handleRouteStart = (url, { shallow }) => {
      let query = {}
      const [path, qs] = url.split('?')
      if (qs) {
        query = parse(qs)
      }

      if (webviewFromRedux?.name) {
        query = {
          ...query,
          webview: webviewFromRedux.name,
          device: webviewFromRedux.device,
          appVersion: webviewFromRedux.appVersion,
          osVersion: webviewFromRedux.osVersion,
        }
      }
      if (webviewFromRedux?.syStaffCode) {
        query.syStaffCode = webviewFromRedux.syStaffCode
      }
      if (webviewFromRedux?.additionalNote) {
        query.additionalNote = webviewFromRedux.additionalNote
      }
      if (webviewFromRedux?.serviceAdminNote) {
        query.serviceAdminNote = webviewFromRedux.serviceAdminNote
      }
      if (webviewFromRedux?.priorityVenderIds?.length) {
        query.pvid = webviewFromRedux.priorityVenderIds
      }

      if (selfConstruct?.reservationCode) {
        // 為了重新整理後還可以繼續對指定客戶繼續操作
        query.reservationCode = selfConstruct.reservationCode
      }
      if (selfConstruct?.scUserId) {
        query.scUserId = selfConstruct.scUserId
      }

      if (preselectVenderFromRedux) {
        query.preselect_vender = preselectVenderFromRedux
      }

      if (!isEmpty(query)) {
        setNextRouteInfo({
          path: path + '?' + stringify(query),
          shallow,
        })
        router.events.emit('routeChangeError')
        throw 'cancelled route change'
      }
    }

    const onDestroy = () => {
      router.events.off('routeChangeStart', handleRouteStart)
    }

    if (nextRouteInfo.path) {
      router.push(nextRouteInfo.path, undefined, {
        shallow: nextRouteInfo.shallow,
      })
      return onDestroy
    }

    router.events.on('routeChangeStart', handleRouteStart)

    return onDestroy
  }, [
    nextRouteInfo.path,
    webviewFromRedux,
    selfConstruct,
    preselectVenderFromRedux,
  ])

  return <>{children}</>
}
